<script lang="ts" setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faBed, faRulerTriangle, faTag } from '@fortawesome/pro-solid-svg-icons';
import { startingPrice } from '@/utilities';
import type { GetProjectDetailsProjectResponse } from '@/api';

const props = defineProps<{
  project: GetProjectDetailsProjectResponse;
}>();

const { t, locale } = useI18n();

// Toutes les fourchettes se calculent à partir des modèles déjà reçus : aucun appel supplémentaire.
const range = (values: number[]) => {
  const filtered = values.filter((value) => value > 0);
  if (filtered.length === 0) return null;

  const min = Math.min(...filtered);
  const max = Math.max(...filtered);
  return min === max ? `${min}` : t('views.project.stats.range', { min, max });
};

const price = computed(() => startingPrice(props.project.models));

const items = computed(() => {
  const models = props.project.models;
  const entries: { key: string; icon: typeof faBed; value: string; label: string }[] = [];
  const uponRequest = t('views.project.stats.upon-request');

  // Un projet non payant n'a pas de prix à afficher du tout : « Sur demande » y promettrait une
  // information que personne ne peut fournir. La mention ne sert qu'aux projets payants sans prix.
  if (props.project.isPaid) {
    entries.push({
      key: 'price',
      icon: faTag,
      value: price.value ? price.value.amount.formatCurrency(locale.value) : uponRequest,
      label: t('views.project.stats.starting-price'),
    });
  }

  const bedroomValues = models.flatMap((model) => [model.bedrooms, model.bedroomsMax]).filter((value): value is number => value !== null && value > 0);
  const hasStudio = models.some((model) => model.bedrooms === 0);
  const bedroomsMax = bedroomValues.length > 0 ? Math.max(...bedroomValues) : null;
  const bedrooms = hasStudio ? (bedroomsMax ? t('views.project.stats.range', { min: t('views.project.stats.loft-studio'), max: bedroomsMax }) : t('views.project.stats.loft-studio')) : range(bedroomValues);
  entries.push({ key: 'bedrooms', icon: faBed, value: bedrooms ?? uponRequest, label: t('views.project.stats.bedrooms') });

  const floorArea = range(models.map((model) => model.floorArea));
  entries.push({
    key: 'floor-area',
    icon: faRulerTriangle,
    value: floorArea ? `${floorArea} ${t('views.project.characteristics.floor-area')}` : uponRequest,
    label: t('views.project.stats.floor-area'),
  });

  return entries;
});
</script>

<template>
  <section v-if="items.length > 0">
    <!-- Empilées en deçà de sm : à trois par ligne sur un téléphone, il ne reste que 63 px de texte
         par tuile et « 650 à 1305 pi² » se fait tronquer. Au-delà, les colonnes se répartissent
         d'elles-mêmes, deux ou trois selon que le prix est affiché ou non. -->
    <dl class="grid grid-cols-1 gap-3 sm:auto-cols-fr sm:grid-flow-col sm:gap-4">
      <div v-for="item in items" :key="item.key" class="flex items-start gap-2 rounded-lg border border-gray-200 bg-gray-50 p-3 sm:p-4">
        <FontAwesomeIcon :icon="item.icon" class="text-ph-green-700 mt-1 h-4 shrink-0" />
        <div class="min-w-0">
          <dd class="truncate text-sm font-semibold text-gray-900 md:text-base">{{ item.value }}</dd>
          <dt class="truncate text-xs text-gray-600">{{ item.label }}</dt>
        </div>
      </div>
    </dl>
  </section>
</template>

<style lang="css" scoped></style>
